import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { Toaster } from "sonner";
import { SEOHead, FAQSchema, BreadcrumbSchema, SpeakableSchema, LocalBusinessSchema } from "./components/SEOHead";

// Components
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import SpecialitiesSection from "./components/SpecialitiesSection";
import FacilitiesSection from "./components/FacilitiesSection";
import TestimonialsSection from "./components/TestimonialsSection";
import AppointmentSection from "./components/AppointmentSection";
import BlogSection from "./components/BlogSection";
import InstagramSection from "./components/InstagramSection";
import FAQSection from "./components/FAQSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import ExitIntentPopup from "./components/ExitIntentPopup";
import GoogleReviewBadge from "./components/GoogleReviewBadge";
import InsuranceSection from "./components/InsuranceSection";
import SocialProofToast from "./components/SocialProofToast";
import StickyCTABar from "./components/StickyCTABar";
import CampsSection from "./components/CampsSection";

import FadeInSection from "./components/FadeInSection";

// Pages
import GynaecologyPage from "./pages/GynaecologyPage";
import UrologyPage from "./pages/UrologyPage";
import CardiologyPage from "./pages/CardiologyPage";
import OrthopaedicsPage from "./pages/OrthopaedicsPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

function HomePage() {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);

    // Scroll event - fires when user scrolls 90% of page
    let scrollFired = false;
    const handleScroll = () => {
      if (scrollFired) return;
      const scrollPercent = (window.scrollY + window.innerHeight) / document.body.scrollHeight;
      if (scrollPercent > 0.9) {
        scrollFired = true;
        if (window.gtag) window.gtag('event', 'scroll', {});
      }
    };

    // User engagement - fires after 10 seconds on page
    const engagementTimer = setTimeout(() => {
      if (window.gtag) window.gtag('event', 'user_engagement', {});
    }, 10000);

    // Click tracking for phone calls
    const handleClick = (e) => {
      const link = e.target.closest('a[href^="tel:"]');
      if (link) {
        if (window.gtag) window.gtag('event', 'click', {});
      }
    };

    // Form start tracking - when user focuses on any form input
    let formStartFired = false;
    const handleFocus = (e) => {
      if (formStartFired) return;
      if (e.target.matches('input, select, textarea')) {
        formStartFired = true;
        if (window.gtag) window.gtag('event', 'form_start', {});
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleClick);
    document.addEventListener('focusin', handleFocus);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('focusin', handleFocus);
      clearTimeout(engagementTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const faqQuestions = t('faq.questions') || [];

  return (
    <>
      <SEOHead
        title="Sakthi Hospital | Best Hospital in Chennai - Triplicane, Royapettah & Mylapore"
        description="Top-rated NABH Accredited Multi-Specialty Hospital in Chennai. 25+ years of compassionate care. Best Gynaecology, Cardiology, Urology & Orthopaedics hospital. 2,00,000+ families trust us. 24/7 Emergency & ICU. Book Free Consultation."
        path="/"
        keywords="best hospital in Chennai, best hospital Triplicane, best hospital Royapettah, top multi-specialty hospital Chennai, NABH hospital Chennai, 24/7 emergency hospital Chennai, best gynecologist Chennai, best cardiologist Chennai, best urologist Chennai, best orthopaedic doctor Chennai, affordable hospital Chennai, hospital near me, maternity hospital Chennai, best doctor in Triplicane"
      />
      <LocalBusinessSchema />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }]} />
      <SpeakableSchema
        headline="Sakthi Hospital - Caring Like Family, Healing With Trust"
        summary="NABH Accredited multi-specialty hospital in Triplicane, Chennai. 25+ years serving 1,50,000+ families."
        path="/"
      />
      <FAQSchema questions={faqQuestions.slice(0, 5)} />

      <Navbar />
      <main>
        <HeroSection />
        <CampsSection />
        <FadeInSection><AboutSection /></FadeInSection>
        <FadeInSection><SpecialitiesSection /></FadeInSection>
        <FadeInSection><FacilitiesSection /></FadeInSection>
        <FadeInSection><TestimonialsSection /></FadeInSection>
        <FadeInSection><InstagramSection /></FadeInSection>
        <FadeInSection><BlogSection /></FadeInSection>
        <FadeInSection><FAQSection /></FadeInSection>
        <InsuranceSection />
        <FadeInSection><ContactSection /></FadeInSection>
      </main>
      <Footer />
      <WhatsAppButton />
      <GoogleReviewBadge />
      <SocialProofToast />
      <StickyCTABar />
      <ExitIntentPopup />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
          <div className="App min-h-screen bg-white" data-testid="app-container">
            <Toaster
              position="top-center"
              richColors
              toastOptions={{ style: { fontFamily: "'Open Sans', sans-serif" } }}
            />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/gynaecology" element={<GynaecologyPage />} />
              <Route path="/urology" element={<UrologyPage />} />
              <Route path="/cardiology" element={<CardiologyPage />} />
              <Route path="/orthopaedics" element={<OrthopaedicsPage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
          </div>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
